import { useCallback, useEffect, useRef, useState } from "react";
import type { TaskEditorState } from "../utils/taskEditorUtils";
import { resumeTaskAuthoring, streamTaskAuthoring } from "./apiClient";
import { clearChat, loadChat, saveChat } from "./chatStorage";
import type {
  AssistantStep,
  AssistantTurn,
  ChatTurn,
  Decision,
  SSEEvent,
  UserTurn,
} from "./types";

const rid = () =>
  Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

const generateThreadId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return rid() + "-" + rid();
};

interface UseAgentChatThreadResult {
  threadId: string | null;
  turns: ChatTurn[];
  isStreaming: boolean;
  send: (message: string, currentTask?: TaskEditorState) => Promise<void>;
  respondToApproval: (turnId: string, decisions: Decision[]) => Promise<void>;
  clear: () => void;
  cancel: () => void;
}

export function useAgentChatThread(
  taskId: string | undefined,
  onTaskPatch: (task: TaskEditorState) => void
): UseAgentChatThreadResult {
  const [threadId, setThreadId] = useState<string | null>(null);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const onTaskPatchRef = useRef(onTaskPatch);

  useEffect(() => {
    onTaskPatchRef.current = onTaskPatch;
  }, [onTaskPatch]);

  // taskId 변경 시 storage 로드
  useEffect(() => {
    if (!taskId) {
      setThreadId(null);
      setTurns([]);
      return;
    }
    const loaded = loadChat(taskId);
    setThreadId(loaded.threadId);
    setTurns(loaded.turns);
  }, [taskId]);

  // turns/threadId 변경 시 storage 저장
  useEffect(() => {
    if (!taskId) return;
    saveChat(taskId, { threadId, turns });
  }, [taskId, threadId, turns]);

  // assistantTurn 갱신 reducer
  const applyEvent = useCallback((turnId: string, event: SSEEvent) => {
    setTurns((prev) =>
      prev.map((turn) => {
        if (turn.role !== "assistant" || turn.id !== turnId) return turn;
        return reduceAssistantTurn(turn, event);
      })
    );
    if (event.event === "task_patch") {
      onTaskPatchRef.current(event.data.task);
    }
  }, []);

  const consumeStream = useCallback(
    async (
      turnId: string,
      stream: AsyncGenerator<SSEEvent>,
      signal: AbortSignal
    ) => {
      try {
        for await (const event of stream) {
          if (signal.aborted) break;
          applyEvent(turnId, event);
        }
      } catch (err) {
        if (signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        setTurns((prev) =>
          prev.map((turn) =>
            turn.role === "assistant" && turn.id === turnId
              ? {
                  ...turn,
                  status: "error",
                  error: { type: "stream_error", message },
                }
              : turn
          )
        );
      } finally {
        setIsStreaming(false);
      }
    },
    [applyEvent]
  );

  const send = useCallback(
    async (message: string, currentTask?: TaskEditorState) => {
      if (isStreaming) return;
      let tid = threadId;
      if (!tid) {
        tid = generateThreadId();
        setThreadId(tid);
      }
      const userTurn: UserTurn = {
        id: rid(),
        role: "user",
        content: message,
        createdAt: Date.now(),
      };
      const assistantTurn: AssistantTurn = {
        id: rid(),
        role: "assistant",
        steps: [],
        status: "streaming",
        taskChanged: false,
        createdAt: Date.now(),
      };
      setTurns((prev) => [...prev, userTurn, assistantTurn]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const stream = streamTaskAuthoring(
        {
          thread_id: tid,
          message,
          current_task: currentTask ?? null,
        },
        controller.signal
      );
      await consumeStream(assistantTurn.id, stream, controller.signal);
    },
    [consumeStream, isStreaming, threadId]
  );

  const respondToApproval = useCallback(
    async (turnId: string, decisions: Decision[]) => {
      if (isStreaming || !threadId) return;
      // 결정 step 추가 + status streaming 으로 변경
      setTurns((prev) =>
        prev.map((turn) => {
          if (turn.role !== "assistant" || turn.id !== turnId) return turn;
          if (!turn.pendingApproval) return turn;
          const actions = turn.pendingApproval.action_requests;
          const decisionSteps: AssistantStep[] = decisions.map(
            (decision, idx) => ({
              kind: "approval_decision",
              id: rid(),
              decision,
              originalAction: actions[idx] ?? actions[0],
            })
          );
          return {
            ...turn,
            steps: [...turn.steps, ...decisionSteps],
            status: "streaming",
            pendingApproval: undefined,
          };
        })
      );

      setIsStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;
      const stream = resumeTaskAuthoring(
        { thread_id: threadId, decisions },
        controller.signal
      );
      await consumeStream(turnId, stream, controller.signal);
    },
    [consumeStream, isStreaming, threadId]
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setThreadId(null);
    setTurns([]);
    setIsStreaming(false);
    if (taskId) clearChat(taskId);
  }, [taskId]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  return {
    threadId,
    turns,
    isStreaming,
    send,
    respondToApproval,
    clear,
    cancel,
  };
}

function reduceAssistantTurn(
  turn: AssistantTurn,
  event: SSEEvent
): AssistantTurn {
  switch (event.event) {
    case "session":
      return turn;

    case "tool_call": {
      const step: AssistantStep = {
        kind: "tool",
        id: rid(),
        name: event.data.name,
        args: event.data.args,
        startedAt: Date.now(),
        status: "running",
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "tool_result": {
      // 같은 name 의 마지막 running step 을 done 처리
      let updated = false;
      const nextSteps = [...turn.steps].reverse().map((step) => {
        if (
          !updated &&
          step.kind === "tool" &&
          step.name === event.data.name &&
          step.status === "running"
        ) {
          updated = true;
          return {
            ...step,
            status: "done" as const,
            result: event.data.preview,
            finishedAt: Date.now(),
          };
        }
        return step;
      });
      nextSteps.reverse();
      return { ...turn, steps: nextSteps };
    }

    case "message_chunk": {
      const last = turn.steps[turn.steps.length - 1];
      if (last?.kind === "text") {
        const merged: AssistantStep = {
          ...last,
          content: last.content + event.data.delta,
        };
        return {
          ...turn,
          steps: [...turn.steps.slice(0, -1), merged],
        };
      }
      const step: AssistantStep = {
        kind: "text",
        id: rid(),
        content: event.data.delta,
      };
      return { ...turn, steps: [...turn.steps, step] };
    }

    case "pending_approval":
      return {
        ...turn,
        status: "awaiting_approval",
        pendingApproval: event.data,
      };

    case "task_patch":
      return { ...turn, taskChanged: true };

    case "final":
      return {
        ...turn,
        status: "complete",
        taskChanged: event.data.task_changed,
      };

    case "debug":
      return {
        ...turn,
        debug: {
          trace_id: event.data.trace_id,
          execution_time_ms: event.data.execution_time_ms,
        },
      };

    case "error":
      return {
        ...turn,
        status: "error",
        error: { type: event.data.type, message: event.data.message },
      };

    default:
      return turn;
  }
}
